import { useEffect, useRef, useState } from 'react';
import { X, RefreshCw, LayoutGrid, CopyPlus, Pin, AppWindow } from 'lucide-react';
import { useTabs } from '@/contexts/TabsContext';
import { findRoute } from '@/routes/routeMap';

/**
 * Bottom tab bar + tab switching.
 *
 * Keyboard (Alt+Tab itself belongs to Windows, so the app uses Alt+Q the same way):
 *   hold Alt, tap Q        -> switcher opens on the last-used tab; tap Q again to move on,
 *                             release Alt to go there (Shift+Q goes back, Esc cancels)
 *   Alt+1 ... Alt+9        -> jump straight to that tab
 *   Alt+W                  -> close the current tab
 * Mobile: swipe left / right on the bar for the next / previous tab, or tap the grid
 * button for a full-screen list of open tabs.
 */
const iconFor = (tab) => tab.icon || findRoute(tab.path)?.icon;

export const TabBar = () => {
  const { tabs, activeId, switchTab, closeTab, refreshTab, cycleTab, mruOrder, duplicateTab, MAX_TABS } = useTabs();
  const [switcher, setSwitcher] = useState(null); // { order: [ids], sel: index }
  const [sheet, setSheet] = useState(false);
  const stateRef = useRef({});
  stateRef.current = { tabs, activeId, switcher, switchTab, closeTab, mruOrder };
  const barRef = useRef(null);
  const touch = useRef(null);

  // keyboard
  useEffect(() => {
    const onDown = (e) => {
      const { tabs: ts, activeId: act, switcher: sw, switchTab: go, closeTab: close, mruOrder: mru } = stateRef.current;
      if (!e.altKey || e.ctrlKey || e.metaKey) {
        if (sw && e.key === 'Escape') setSwitcher(null);
        return;
      }
      if (e.code === 'KeyQ') {
        e.preventDefault();
        if (ts.length < 2) return;
        if (!sw) {
          const order = mru();
          setSwitcher({ order, sel: e.shiftKey ? order.length - 1 : 1 });
        } else {
          const n = sw.order.length;
          setSwitcher({ ...sw, sel: (sw.sel + (e.shiftKey ? -1 : 1) + n) % n });
        }
        return;
      }
      if (/^Digit[1-9]$/.test(e.code)) {
        const t = ts[Number(e.code.slice(5)) - 1];
        if (t) {
          e.preventDefault();
          go(t.id);
        }
        return;
      }
      if (e.code === 'KeyW') {
        const t = ts.find((x) => x.id === act);
        if (t && !t.pinned) {
          e.preventDefault();
          close(t.id);
        }
      }
    };
    const onUp = (e) => {
      const { switcher: sw, switchTab: go } = stateRef.current;
      if (sw && (e.key === 'Alt' || !e.altKey)) {
        const id = sw.order[sw.sel];
        setSwitcher(null);
        if (id) go(id);
      }
    };
    const onBlur = () => setSwitcher(null);
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // keep the active chip in view
  useEffect(() => {
    const el = barRef.current?.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeId]);

  if (!tabs || tabs.length === 0) return null;

  // swipe on the bar: a quick horizontal flick changes tab; a slow drag still scrolls the chips
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, at: Date.now(), scroll: barRef.current?.scrollLeft || 0 };
  };
  const onTouchEnd = (e) => {
    const s = touch.current;
    touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const scrolled = Math.abs((barRef.current?.scrollLeft || 0) - s.scroll) > 8;
    if (!scrolled && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && Date.now() - s.at < 600) {
      cycleTab(dx < 0 ? 1 : -1);
    }
  };

  const chip = (tab, i) => {
    const Icon = iconFor(tab);
    const active = tab.id === activeId;
    return (
      <div
        key={tab.id}
        className={`group relative flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md shrink-0 cursor-pointer transition-all ${
          active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
        onClick={() => switchTab(tab.id)}
        data-testid={`tab-${tab.id}`}
        data-active={active ? 'true' : 'false'}
        role="tab"
        aria-selected={active}
        title={i < 9 ? `${tab.label}  (Alt+${i + 1})` : tab.label}
      >
        {Icon && <Icon size={14} className="shrink-0" />}
        <span className="text-xs sm:text-sm font-medium whitespace-nowrap max-w-[110px] sm:max-w-[160px] truncate">{tab.label}</span>
        {tab.pinned && tab.id !== 'tab-home' && <Pin size={10} className="shrink-0 opacity-50" />}
        {active && tab.stale && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              refreshTab(tab.id);
            }}
            className="ml-1 p-0.5 rounded hover:bg-white/20"
            title="Refresh — data may have changed"
            data-testid={`tab-refresh-${tab.id}`}
            aria-label="Refresh tab"
          >
            <RefreshCw size={11} />
          </button>
        )}
        {!tab.pinned && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeTab(tab.id);
            }}
            className={`ml-0.5 p-0.5 rounded transition-opacity ${active ? 'hover:bg-white/20 opacity-90' : 'opacity-60 hover:opacity-100 hover:bg-slate-300'}`}
            title="Close tab (Alt+W)"
            data-testid={`tab-close-${tab.id}`}
            aria-label="Close tab"
          >
            <X size={12} />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
        data-testid="tab-bar"
        aria-label="Open tabs"
      >
        <div className="flex items-stretch">
          <div
            ref={barRef}
            className="flex-1 flex items-stretch overflow-x-auto px-1 py-1 gap-1 scrollbar-none"
            style={{ WebkitOverflowScrolling: 'touch' }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {tabs.map(chip)}
          </div>
          <button
            type="button"
            className="flex items-center gap-1 px-2 border-l border-slate-200 text-slate-600 hover:bg-slate-100"
            onClick={() => duplicateTab()}
            title="Open another copy of this page (e.g. two Stock Matrix tabs to compare)"
            data-testid="tab-duplicate"
          >
            <CopyPlus size={16} />
            <span className="hidden lg:inline text-xs">Copy tab</span>
          </button>
          <button
            type="button"
            className="hidden md:flex items-center gap-1 px-2 border-l border-slate-200 text-slate-600 hover:bg-slate-100"
            onClick={() => {
              const t = tabs.find((x) => x.id === activeId);
              window.open(`${window.location.origin}${t ? t.path : '/'}`, '_blank', 'popup=yes,width=1280,height=860');
            }}
            title="Open this page in a separate window (put two windows side by side)"
            data-testid="tab-new-window"
          >
            <AppWindow size={16} />
            <span className="hidden lg:inline text-xs">New window</span>
          </button>
          <div className="hidden md:flex items-center pr-2 pl-1 text-[10px] text-slate-400 whitespace-nowrap" title="Hold Alt and tap Q to switch tabs, like Alt+Tab">
            Alt+Q switch · {tabs.length}/{MAX_TABS}
          </div>
          <button
            type="button"
            className="md:hidden flex items-center gap-1 px-3 border-l border-slate-200 text-slate-600 active:bg-slate-100"
            onClick={() => setSheet(true)}
            aria-label="All open tabs"
            data-testid="tab-sheet-button"
          >
            <LayoutGrid size={18} />
            <span className="text-xs font-semibold">{tabs.length}</span>
          </button>
        </div>
      </nav>

      {/* Alt+Q switcher */}
      {switcher && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 pointer-events-none" data-testid="tab-switcher">
          <div className="bg-white rounded-xl shadow-2xl p-3 flex flex-wrap justify-center gap-2 max-w-[90vw]">
            {switcher.order.map((id, i) => {
              const tab = tabs.find((t) => t.id === id);
              if (!tab) return null;
              const Icon = iconFor(tab);
              const sel = i === switcher.sel;
              return (
                <div
                  key={id}
                  className={`w-32 h-24 rounded-lg border-2 flex flex-col items-center justify-center gap-2 px-2 text-center ${
                    sel ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600'
                  }`}
                >
                  {Icon && <Icon size={26} />}
                  <span className="text-xs font-medium leading-tight line-clamp-2">{tab.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile: all tabs sheet */}
      {sheet && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:hidden" onClick={() => setSheet(false)} data-testid="tab-sheet">
          <div className="w-full bg-white rounded-t-2xl p-4 pb-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Open tabs ({tabs.length}/{MAX_TABS})</div>
              <button type="button" className="p-1 text-slate-500" onClick={() => setSheet(false)} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {tabs.map((tab) => {
                const Icon = iconFor(tab);
                const active = tab.id === activeId;
                return (
                  <div
                    key={tab.id}
                    className={`relative rounded-xl border-2 p-3 h-24 flex flex-col justify-between ${active ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200'}`}
                    onClick={() => {
                      switchTab(tab.id);
                      setSheet(false);
                    }}
                  >
                    {Icon && <Icon size={22} className={active ? 'text-indigo-700' : 'text-slate-500'} />}
                    <span className="text-sm font-medium leading-tight line-clamp-2 pr-5">{tab.label}</span>
                    {!tab.pinned && (
                      <button
                        type="button"
                        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/80 text-slate-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeTab(tab.id);
                        }}
                        aria-label="Close tab"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-2 text-sm text-slate-600"
              onClick={() => {
                duplicateTab();
                setSheet(false);
              }}
            >
              <CopyPlus size={16} /> Open another copy of the current page
            </button>
            <p className="text-[11px] text-slate-400 mt-3">Tip: swipe left or right on the tab bar to move between tabs. Long-press a menu item to open it in a new tab.</p>
          </div>
        </div>
      )}
    </>
  );
};
